// Inspecteur · Verwerkingen — the Verwerkingenregister (Art. 30 RoPA) LIST, rebuilt on shadcn/ui
// (FRONTEND-BLUEPRINT §2.3 + §3.1). Fixed Art.30 columns via the shared DataTable; a draft-then-promote
// create (Sheet: Naam, Verhoogd risico + Incidenteel switches, dual role) using createVerwerking, plus
// row-level Bewerken (edit, same Sheet → PATCH) and Archiveren (M3 — set status=archived; the Art.30 record
// is retained for accountability, not hard-deleted). Row click → detail. RLS scopes rows server-side.
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { MoreHorizontal, Pencil, Archive } from "lucide-react";
import { ApiError } from "@sympllex/api-client";
import type { CreateVerwerkingInput } from "@sympllex/api-client";
import { api } from "../../lib/api";
import { Can } from "../../lib/rbac";
import { useSession } from "../../lib/session";
import { hasPermission } from "@sympllex/auth";
import { useI18n } from "../../lib/i18n";
import { enumLabel } from "../../lib/enum-labels";
import { PageHeader } from "@/components/page-header";
import { DataTable, type Column } from "@/components/data-table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type Row = Record<string, unknown>;

const errText = (e: unknown) => (e instanceof ApiError ? `Server ${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message);
const isTrue = (v: unknown) => v === true || v === "true" || v === 1;

export function VerwerkingenRoute() {
  const { t } = useI18n();
  const session = useSession();
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({ queryKey: ["verwerkingen"], queryFn: () => api.listVerwerkingen() });
  const rows = (data ?? []) as Row[];

  // `editing`: null = closed · "new" = create · Row = edit that row. `archiving`: the row pending archive.
  const [editing, setEditing] = useState<Row | "new" | null>(null);
  const [archiving, setArchiving] = useState<Row | null>(null);
  const canWrite = hasPermission(session, "verwerking.update");

  // Art.30 register columns (fixed, FRONTEND-BLUEPRINT §3.1): Naam · Status · Verhoogd risico ·
  // Incidenteel · Verantwoordelijke · Verwerker. Verantwoordelijke/Verwerker are derived from
  // actingOrgRole; heightened-risk/incidental render from the row when present (else "—").
  const yesNo = (v: unknown) => <Badge variant={isTrue(v) ? "default" : "outline"}>{isTrue(v) ? t("verw.yes") : t("verw.no")}</Badge>;
  // L4: controller/processor are independent capabilities (read the booleans; fall back to the legacy enum).
  const isController = (r: Row) => (r.actsAsController !== undefined ? isTrue(r.actsAsController) : String(r.actingOrgRole ?? "") !== "processor");
  const isProcessor = (r: Row) => (r.actsAsProcessor !== undefined ? isTrue(r.actsAsProcessor) : String(r.actingOrgRole ?? "") === "processor");
  const columns: Column<Row>[] = [
    { key: "name", header: t("verw.col.name"), render: (r) => <span className="font-medium">{String(r.name ?? "—")}</span> },
    { key: "status", header: t("verw.col.status"), render: (r) => <Badge variant="secondary">{enumLabel(t, "verwerkingStatus", r.status)}</Badge> },
    { key: "effectiveFrom", header: t("verw.col.effectiveFrom"), render: (r) => <span className="text-muted-foreground">{r.effectiveFrom ? String(r.effectiveFrom) : "—"}</span> },
    { key: "heightenedRisk", header: t("verw.col.heightenedRisk"), align: "center", render: (r) => yesNo(r.heightenedRisk ?? r.verhoogdRisico) },
    { key: "incidental", header: t("verw.col.incidental"), align: "center", render: (r) => yesNo(r.incidental ?? r.incidenteel) },
    { key: "controller", header: t("verw.col.controller"), align: "center", render: (r) => (isController(r) ? "✓" : "—") },
    { key: "processor", header: t("verw.col.processor"), align: "center", render: (r) => (isProcessor(r) ? "✓" : "—") },
  ];

  return (
    <section>
      <PageHeader
        title={t("page.verwerkingen")}
        description={t("verw.sub")}
        help={t("help.verwerkingen")}
        actions={<Can perm="verwerking.write"><span className="flex items-center gap-2">{rows.length === 0 && !isLoading && <StarterButton />}<Button onClick={() => setEditing("new")}>{t("verw.add")}</Button></span></Can>}
      />
      {error ? (
        <p className="text-sm text-destructive">{t("crud.failed")} {errText(error)}</p>
      ) : (
        <DataTable<Row>
          columns={columns}
          rows={rows}
          loading={isLoading}
          empty={t("verw.none")}
          getRowId={(r) => String(r.id)}
          onRowClick={(r) => { if (r.id) void navigate({ to: "/verwerkingen/$id", params: { id: String(r.id) } }); }}
          {...(canWrite ? {
            rowActions: (r: Row) => (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label={t("verw.rowactions")}><MoreHorizontal className="size-4" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => setEditing(r)}><Pencil className="size-4" />{t("verw.action.edit")}</DropdownMenuItem>
                  <DropdownMenuItem variant="destructive" onSelect={() => setArchiving(r)}><Archive className="size-4" />{t("verw.action.archive")}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ),
          } : {})}
        />
      )}

      {/* Shared create/edit Sheet — keyed so each open re-mounts with fresh, correctly-seeded state. */}
      {editing !== null && (
        <VerwerkingFormSheet
          key={editing === "new" ? "new" : String((editing as Row).id)}
          row={editing === "new" ? null : (editing as Row)}
          onClose={() => setEditing(null)}
        />
      )}

      {archiving && (
        <ArchiveDialog row={archiving} onClose={() => setArchiving(null)} />
      )}
    </section>
  );
}

// L11 — one-click "Gouden Standaard" starter: seeds the 11 baseline processings into an empty register
// (the legacy setup-wizard behaviour). Shown only when the register is empty.
function StarterButton() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => api.applyStarterTemplate(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["verwerkingen"] }); },
  });
  return (
    <Button variant="outline" disabled={mut.isPending} onClick={() => mut.mutate()} title={t("verw.starter.hint")}>
      {mut.isPending ? t("verw.starter.applying") : t("verw.starter")}
    </Button>
  );
}

// Shared draft-create / edit Sheet (FRONTEND-BLUEPRINT §3.1): Naam + Verhoogd risico / Incidenteel switches +
// the dual acting-role switches (L4 — a processing can be controller AND processor) + Actief sinds. Create
// (row=null): "Opslaan" stays on the list, "Opslaan en openen" opens the new record. Edit (row set): a single
// "Opslaan" PATCHes and closes. Controlled via `onClose`; open is true while mounted.
function VerwerkingFormSheet({ row, onClose }: { row: Row | null; onClose: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isEdit = row !== null;
  const [name, setName] = useState(isEdit ? String(row!.name ?? "") : "");
  const [heightenedRisk, setHeightenedRisk] = useState(isEdit ? isTrue(row!.heightenedRisk ?? row!.verhoogdRisico) : false);
  const [incidental, setIncidental] = useState(isEdit ? isTrue(row!.incidental ?? row!.incidenteel) : false);
  const [actsAsController, setActsAsController] = useState(
    isEdit ? (row!.actsAsController !== undefined ? isTrue(row!.actsAsController) : String(row!.actingOrgRole ?? "") !== "processor") : true,
  );
  const [actsAsProcessor, setActsAsProcessor] = useState(
    isEdit ? (row!.actsAsProcessor !== undefined ? isTrue(row!.actsAsProcessor) : String(row!.actingOrgRole ?? "") === "processor") : false,
  );
  const [effectiveFrom, setEffectiveFrom] = useState(isEdit && row!.effectiveFrom ? String(row!.effectiveFrom) : "");

  const mutation = useMutation({
    mutationFn: () => {
      const input: CreateVerwerkingInput = {
        name: name.trim(), heightenedRisk, incidental, actsAsController, actsAsProcessor,
        ...(effectiveFrom ? { effectiveFrom } : isEdit ? { effectiveFrom: null } : {}),
      };
      return isEdit ? api.updateVerwerking(String(row!.id), input as unknown as Record<string, unknown>) : api.createVerwerking(input);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["verwerkingen"] }); },
  });

  const submit = (thenOpen: boolean) => {
    mutation.mutate(undefined, {
      onSuccess: (saved) => {
        const id = (saved as { id?: string } | null)?.id ?? (isEdit ? String(row!.id) : undefined);
        onClose();
        if (thenOpen && id) void navigate({ to: "/verwerkingen/$id", params: { id } });
      },
    });
  };

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{isEdit ? t("verw.edit.title") : t("verw.create.title")}</SheetTitle>
          <SheetDescription>{isEdit ? t("verw.edit.desc") : t("verw.create.desc")}</SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-5 px-6">
          <div className="grid gap-2">
            <Label htmlFor="verw-name">{t("verw.field.name")}</Label>
            <Input id="verw-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="grid gap-0.5">
              <Label htmlFor="verw-risk">{t("verw.field.heightenedRisk")}</Label>
              <span className="text-xs text-muted-foreground">{t("verw.field.heightenedRisk.hint")}</span>
            </div>
            <Switch id="verw-risk" checked={heightenedRisk} onCheckedChange={setHeightenedRisk} />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="grid gap-0.5">
              <Label htmlFor="verw-incidental">{t("verw.field.incidental")}</Label>
              <span className="text-xs text-muted-foreground">{t("verw.field.incidental.hint")}</span>
            </div>
            <Switch id="verw-incidental" checked={incidental} onCheckedChange={setIncidental} />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="verw-from">{t("verw.field.effectiveFrom")}</Label>
            <Input id="verw-from" type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
          </div>

          <div className="grid gap-2">
            <Label>{t("verw.field.role")}</Label>
            <span className="text-xs text-muted-foreground">{t("verw.field.role.hint")}</span>
            <Label htmlFor="role-controller" className="flex items-center gap-2 font-normal">
              <Switch id="role-controller" checked={actsAsController} onCheckedChange={setActsAsController} />
              {t("verw.role.controller")}
            </Label>
            <Label htmlFor="role-processor" className="flex items-center gap-2 font-normal">
              <Switch id="role-processor" checked={actsAsProcessor} onCheckedChange={setActsAsProcessor} />
              {t("verw.role.processor")}
            </Label>
          </div>

          {mutation.isError && <p className="text-sm text-destructive">{errText(mutation.error)}</p>}
        </div>

        <SheetFooter className="flex-row justify-end gap-2">
          <Button variant="outline" disabled={mutation.isPending || !name.trim()} onClick={() => submit(false)}>
            {mutation.isPending ? t("verw.saving") : t("verw.save")}
          </Button>
          {!isEdit && (
            <Button disabled={mutation.isPending || !name.trim()} onClick={() => submit(true)}>
              {t("verw.saveOpen")}
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// M3 — archive = set status to "archived" (retain the Art.30 record for accountability; not a hard delete).
// Reachable through the `update` verb the role already holds; the register filters by status downstream.
function ArchiveDialog({ row, onClose }: { row: Row; onClose: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => api.updateVerwerking(String(row.id), { status: "archived" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["verwerkingen"] }); onClose(); },
  });
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !mut.isPending) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("verw.archive.title")}</DialogTitle>
          <DialogDescription>{t("verw.archive.body")}</DialogDescription>
        </DialogHeader>
        <p className="text-sm font-medium">{String(row.name ?? "")}</p>
        {mut.isError && <p className="text-sm text-destructive">{errText(mut.error)}</p>}
        <DialogFooter className="gap-2">
          <Button variant="outline" disabled={mut.isPending} onClick={onClose}>{t("crud.cancel")}</Button>
          <Button variant="destructive" disabled={mut.isPending} onClick={() => mut.mutate()}>
            {mut.isPending ? t("verw.archive.archiving") : t("verw.archive.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
